<template>
  <page v-loading="isLoading">
    <page-header-new
      class="page-header"
      :title="campaignName"
      :breadcrumbs="breadcrumbs"
    >
      <template v-slot:actions>
        <channel-select :platform="true" @change="fetchData" />
        <date-filter @change="handlerDateFilter($event)" />
      </template>
    </page-header-new>
    <page-content class="campaign-overview">
      <h3 class="title" v-if="isMetricsAvailable">
        {{ $t('CampaignOverview_GoalMetrics') }}
      </h3>
      <div class="metrics-ct" v-if="isMetricsAvailable">
        <div class="metric" v-for="item in data.goal">
          <key-metric
            :metric="item.value"
            :type="item.type"
            :title="
              metricLabels.hasOwnProperty(item.name)
                ? $t(metricLabels[item.name])
                : item.name
            "
          />
        </div>
      </div>
      <h3 class="title">
        {{ $t('CampaignOverview_Channels') }}
      </h3>
      <el-tag effect="plain" v-if="filteredChannels" size="mini">
        {{
          $t('Dashboard_FilteredChannels', {
            number: filteredChannels
          })
        }}
      </el-tag>
      <p v-if="isWorkflowCampaign" class="workflow-channel-tooltip">
        {{ $t('AnalyticsCampaignsPage_Placeholder') }}
      </p>
      <el-row class="channels">
        <template v-if="hasData">
          <el-col
            class="channel"
            :key="channel.name"
            :span="12"
            v-for="channel in data.channels"
          >
            <channel-metrics
              :icon="`dashboard_${channel.name}`"
              :title="
                channelLabels.hasOwnProperty(channel.name)
                  ? $t(channelLabels[channel.name])
                  : channel.name
              "
              :button-title="$t('CampaignOverview_CTA_Title')"
              :button-action="generateRoute.bind(this, channel.name)"
            >
              <el-row slot="metrics">
                <el-col
                  :span="8"
                  :key="metric.name"
                  v-for="metric in channel.metrics"
                >
                  <metric
                    class="channel-metric"
                    :title="
                      metricLabels.hasOwnProperty(metric.name)
                        ? $t(metricLabels[metric.name])
                        : metric.name
                    "
                    :metric="metric.value"
                    :type="metric.type"
                  />
                </el-col>
              </el-row>
            </channel-metrics>
          </el-col>
        </template>
        <template v-else>
          <el-col class="channel">
            <project-overview-placeholder />
          </el-col>
        </template>
      </el-row>

      <el-row class="end-actions" v-if="isWorkflowCampaign">
        <el-col>
          <h3 class="title">{{ $t('CampaignOverview_EndActionsMetrics') }}</h3>
          <rest-list
            :payload="endActionsDataEndpoint"
            :key="key"
            @dataLoaded="setEndActionsData($event)"
            ref="list"
            :pagination="false"
            :empty-text="$t('CampaignOverview_EndActionsMetrics_NoDataFound')"
          >
            <template v-if="endActionsData[0]">
              <rest-list-column
                prop="fields.name"
                :title="$t('CampaignOverview_Table_EndActionName')"
                :sortable="false"
                :filter-input="false"
                :min-width="200"
                :fixed="true"
              />
              <rest-list-column
                prop="fields.variant.key"
                v-if="variants"
                :title="$t('CampaignOverview_Table_EndActionVariant')"
                :min-width="200"
                :filter-input="false"
              >
                <template v-if="row.fields.variant.title" slot-scope="{ row }">
                  <span
                    :class="{ winner: parseInt(row.fields.variant.winner) }"
                  >
                    {{ row.fields.variant.key }} –
                    {{ row.fields.variant.title }}
                  </span>
                  <i
                    v-if="parseInt(row.fields.variant.winner)"
                    class="winner el-icon-trophy"
                  />
                </template>
              </rest-list-column>
              <rest-metric-column
                v-for="(metric, index) in endActionsData[0].stats.metrics"
                :key="index"
                prop="stats"
                :index="index"
                :metric="metric"
                :metric-group="endActionsData[0].stats"
              />
            </template>
          </rest-list>
        </el-col>
      </el-row>

      <el-row class="variants" v-if="variants">
        <el-col>
          <h3 class="title">{{ $t('CampaignOverview_VariantMetrics') }}</h3>
          <rest-list
            :payload="variantDataEndpoint"
            :key="key"
            @dataLoaded="setVariantData($event)"
            ref="list"
          >
            <template v-if="variantData[0]">
              <rest-list-column
                prop="fields.id"
                :title="$t('CampaignOverview_Table_VariantName')"
                :sortable="false"
                :filter-input="false"
                :fixed="true"
                :width="variantWidth"
              >
                <template v-if="row.fields.title" slot-scope="{ row }">
                  <i v-if="row.fields.id === 'W'" class="el-icon-trophy" />
                  <span v-else>{{ row.fields.id }}</span> –
                  {{ row.fields.title }}
                  <i
                    v-if="parseInt(row.fields.winner)"
                    class="winner el-icon-trophy"
                  />
                </template>
              </rest-list-column>
              <rest-list-column
                v-if="hasCustomVariantsShare"
                prop="fields.split_percentage"
                :title="$t('CampaignOverview_VariantSplit_Percentage')"
                :sortable="false"
                :filter-input="false"
                :fixed="true"
                :width="variantWidth"
              >
                <template slot-scope="{ row }">
                  {{ row.fields.split_percentage }}
                </template>
              </rest-list-column>
              <rest-metric-group
                v-for="(metricGroup, metricIndex) in variantData[0]
                  .metricGroups"
                :key="metricIndex"
                :metric-group="metricGroup"
                :prop="'metricGroups[' + metricIndex + ']'"
                align="left"
                :custom-props="{ default: { sortable: false } }"
                :width="metricIndex ? 120 : 200"
              />
            </template>
          </rest-list>
        </el-col>
      </el-row>
    </page-content>
  </page>
</template>

<script>
import PageContent from '@/common/components/page/PageContent'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import {
  getDateFilterRange,
  projectEndpoint,
  projectRoute
} from '@/core/helpers'
import DateFilter from '@/common/components/dashboard/DateFilter'
import moment from 'moment'
import Page from '@/common/components/page/Page'
import RestApi from '@/core/services/RestApi'
import KeyMetric from '@/common/components/KeyMetric'
import {
  metricLabels,
  channelLabels
} from '@/common/components/dashboard/Labels'
import ChannelMetrics from '@/common/components/ChannelMetrics'
import Metric from '@/common/components/Metric'
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestMetricGroup from '@/common/components/rest/RestMetricGroup'
import ChannelSelect from '@/common/components/analytics/ChannelSelect'
import { mapGetters } from 'vuex'
import ProjectOverviewPlaceholder from '@/common/components/ProjectOverviewPlaceholder'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import RestMetricColumn from '@/common/components/rest/RestMetricColumn'

export default {
  name: 'CampaignOverviewPage',
  components: {
    ChannelSelect,
    RestMetricColumn,
    RestMetricGroup,
    RestListColumn,
    RestList,
    Metric,
    ChannelMetrics,
    KeyMetric,
    Page,
    DateFilter,
    PageHeaderNew,
    PageContent,
    ProjectOverviewPlaceholder
  },
  data() {
    return {
      filteredChannels: 0,
      isLoading: false,
      data: [],
      variantData: [],
      endActionsData: [],
      metricLabels,
      channelLabels,
      campaignName: '',
      campaignType: null,
      breadcrumbs: [],
      variants: null,
      date_from: '',
      date_to: '',
      key: '',
      hasData: false,
      hasCustomVariantsShare: false,
      campaignTypeEnum
    }
  },
  created() {
    let dateRange = getDateFilterRange()
    this.handlerDateFilter(dateRange)
  },

  methods: {
    handlerDateFilter(dateRange) {
      this.date_from = moment(dateRange.start).format('YYYY-MM-DD')
      this.date_to = moment(dateRange.end).format('YYYY-MM-DD')
      this.fetchData()
    },
    fetchData() {
      this.isLoading = true

      RestApi(
        projectEndpoint({
          method: 'get',
          url: `campaigns/${this.$route.query.id}/analytics`,
          params: {
            platforms: this.userConfig.show_channel_platforms,
            date_from: this.date_from,
            date_to: this.date_to
          }
        })
      ).then(({ data }) => {
        this.data = data
        this.filteredChannels = 0
        if (this.userConfig.channels && data.channels.length > 0) {
          let availableChannels = Object.keys(this.userConfig.channels)

          this.data.channels = data.channels.filter(channel => {
            let name = this.userConfig.show_channel_platforms
              ? availableChannels.filter(ch => channel.name.includes(ch))[0]
              : channel.name
            if (name && this.userConfig.channels[name]) {
              return true
            }
            this.filteredChannels += 1
          })
        }
        this.isLoading = false
        this.hasData = this.data.channels.length > 0
      })

      this.key = new Date().getTime() // refresh the table items
    },

    generateRoute(channel) {
      this.$router.push(
        projectRoute({
          name: 'StatisticsTab',
          query: {
            id: this.campaignId,
            channel: channel,
            date_from: this.date_from,
            date_to: this.date_to,
            platforms: this.userConfig.show_channel_platforms
          }
        })
      )
    },
    setVariantData(data) {
      this.variantData = [...data]
    },
    setEndActionsData(data) {
      this.endActionsData = [...data]
    }
  },

  computed: {
    ...mapGetters(['userConfig']),
    variantWidth() {
      let width = null
      this.variantData.forEach(row => {
        if (row.fields.title.length) {
          width = 250
        }
      })
      return width
    },
    campaignId() {
      return this.$route.query.id || null
    },
    isMetricsAvailable() {
      return (
        this.data.hasOwnProperty('goal') &&
        this.data.goal !== undefined &&
        this.data.goal.length !== 0
      )
    },
    isWorkflowCampaign() {
      return this.campaignType === campaignTypeEnum.TYPE_WORKFLOW
    },
    endActionsDataEndpoint() {
      if (!this.key) {
        // Prevents duplicate call before key params are set
        return
      }
      return projectEndpoint({
        method: 'get',
        url: `campaigns/${this.$route.query.id}/analytics/end-actions`,
        params: {
          date_from: this.date_from,
          date_to: this.date_to
        }
      })
    },
    variantDataEndpoint() {
      if (!this.key) {
        // Prevents duplicate call before key params are set
        return
      }
      return projectEndpoint({
        method: 'get',
        url: `campaigns/${this.$route.query.id}/analytics/variants`,
        params: {
          date_from: this.date_from,
          date_to: this.date_to
        }
      })
    }
  },
  mounted() {
    const endpoint = {
      method: 'get',
      url: `campaigns/${this.campaignId}`,
      params: { expand: 'ab_variants,type,custom_variant_share' }
    }
    RestApi(projectEndpoint(endpoint)).then(({ data }) => {
      this.campaignName = data.title
      this.variants = data.ab_variants
      this.campaignType = data.type
      this.hasCustomVariantsShare = !!data.custom_variant_share
      this.breadcrumbs = [
        {
          label: this.$t('Analytics_Breadcrumb_Analytics')
        },
        {
          label: this.$t('Analytics_Breadcrumb_Campaigns'),
          route: projectRoute({ name: 'AnalyticsCampaigns' })
        },
        { label: data.title }
      ]
    })
  }
}
</script>

<style scoped lang="scss">
.campaign-overview {
  width: 1092px;
  margin-left: auto;
  margin-right: auto;

  i.winner {
    float: right;
    font-size: 18px;
  }

  .title {
    display: inline-block;
    padding-bottom: 12px;
  }
  .workflow-channel-tooltip {
    padding-bottom: 15px;
  }
  .metrics-ct {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;

    .metric {
      padding-bottom: 20px;
    }

    .metric > div {
      width: 260px;
      margin-right: 11px;
    }
  }
  .channels {
    flex-flow: row wrap;
    display: flex;
    .channel {
      padding-bottom: 20px;
      /deep/ .el-card {
        height: 100%;
      }

      .channel-metric {
        padding-bottom: 24px;
      }
    }
  }
  .end-actions {
    padding-bottom: 20px;
  }
  .empty-channels {
    font-size: 15px;
    color: #acb0b5;
    padding-bottom: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/analytics/pages/CampaignOverviewPage.vue